<template>
  <table class="nested-json-table">
    <tbody>
      <!-- If data is an object, iterate its keys -->
      <template v-if="isObject(data)">
        <tr v-for="key in visibleObjectKeys" :key="key">
          <td class="key">{{ key }}</td>
          <td class="value">
            <!-- If the value is an object or array, render recursively -->
            <NestedJsonTable
              v-if="isObject(data[key]) || isArray(data[key])"
              :data="data[key]"
              :nestedValue="true"
            />
            <span v-else>{{ data[key] }}</span>
          </td>
        </tr>
        <tr v-if="toggleButtonVisible && !nestedValue">
          <td colspan="2">
            <button @click="toggleShow">
              {{ showAll ? 'Show Less' : 'Show More' }}
            </button>
          </td>
        </tr>
      </template>

      <!-- If data is an array, iterate its items -->
      <template v-else-if="isArray(data)">
        <tr v-for="(item, index) in visibleArrayItems" :key="index">
          <td class="value">
            <NestedJsonTable
              v-if="isObject(item) || isArray(item)"
              :data="item"
              :nestedValue="true"
            />
            <span v-else>{{ item }}</span>
          </td>
        </tr>
        <tr v-if="arrayToggleVisible && !nestedValue">
          <td colspan="2">
            <button @click="toggleShow">
              {{ showAll ? 'Show Less' : 'Show More' }}
            </button>
          </td>
        </tr>
      </template>

      <!-- If data is neither an object nor an array -->
      <template v-else>
        <tr>
          <td class="key"></td>
          <td class="value">{{ data }}</td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script>
export default {
  name: 'NestedJsonTable',
  props: {
    data: {
      type: [Object, Array, String, Number, Boolean],
      required: true,
    },
    allowedKeys: {
      type: Array,
      default: () => []  
    },
    nestedValue: {
      type: Boolean,
      default: false
    },
    initialLimit: {
      type: Number,
      default: 5
    }
  },
  data() {
    return {
      showAll: false,
    };
  },
  computed: {
    isTopLevel() {
      return !this.$parent || this.$parent.$options.name !== 'NestedJsonTable';
    },
    allKeys() {
      return this.isObject(this.data) ? Object.keys(this.data) : [];
    },
    allowedKeysSet() {
      return new Set(this.allowedKeys);
    },
    visibleObjectKeys() {
      if (!this.isObject(this.data)) return [];
      // If no allowedKeys specified, show all keys
      if (this.allowedKeys.length === 0) {
        return this.allKeys;
      }
      // Show keys in the same order as allowedKeys
      const primary = this.allowedKeys.filter(key => this.allKeys.includes(key));
      // Append other keys when nestedValue or showAll
      if (this.nestedValue || this.showAll) {
        const hidden = this.allKeys.filter(key => !primary.includes(key));
        return [...primary, ...hidden];
      }
      return primary;
    },
    remainingObjectKeys() {
      return this.allKeys.filter(key => !this.allowedKeysSet.has(key));
    },
    toggleButtonVisible() {
      if (this.allowedKeys.length === 0) return false;
      return this.remainingObjectKeys.length > 0;
    },
    arrayToggleVisible() {
      return this.isArray(this.data) && this.data.length > this.initialLimit;
    },
    visibleArrayItems() {
      if (!this.isArray(this.data)) return [];
      return this.showAll ? this.data : this.data.slice(0, this.initialLimit);
    },
    allAllowed() {
      return this.allKeys.every(key => this.allowedKeysSet.has(key));
    },
  },
  methods: {
    isObject(value) {
      return value && typeof value === 'object' && !Array.isArray(value);
    },
    isArray(value) {
      return Array.isArray(value);
    },
    toggleShow() {
      this.showAll = !this.showAll;
    }
  }
};
</script>

<style scoped>
.nested-json-table {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 1em;
  font-family: Arial, sans-serif;
  font-size: 0.8rem;
}
.nested-json-table th,
.nested-json-table td {
  border: 1px solid #ddd;
  padding: 4px 8px;
  vertical-align: top;
}
.nested-json-table th {
  background-color: #f2f2f2;
  text-align: left;
}
.key {
  font-weight: bold;
  width: 25%;
}
.value {
  width: 75%;
}
</style>
